import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  LiveClientTypingMessage,
  LiveTypingState,
  PublicProfile,
} from '@molt/shared';
import type { LiveSocketFrame } from './socket';

export const COMMENT_TYPING_REFRESH_MS = 5_000;

export interface CommentTypingEntry {
  readonly user: PublicProfile;
  readonly expiresAt: string;
}

interface StoredCommentTypingEntry extends CommentTypingEntry {
  readonly expiresAtMs: number;
}

export type CommentTypingStateSender = (state: LiveTypingState) => void;

export function typingMessage(state: LiveTypingState): LiveClientTypingMessage {
  return { type: 'comment.typing', state };
}

export function useCommentTypingPublisher(
  sendTypingState: CommentTypingStateSender | undefined,
): {
  readonly noteDraftValue: (value: string) => void;
  readonly stopTyping: () => void;
} {
  const sendRef = useRef(sendTypingState);
  const activeRef = useRef(false);
  const lastStartedAtRef = useRef(0);

  useEffect(() => {
    sendRef.current = sendTypingState;
  }, [sendTypingState]);

  const send = useCallback((state: LiveTypingState): void => {
    sendRef.current?.(state);
  }, []);

  const stopTyping = useCallback(() => {
    if (!activeRef.current) return;
    activeRef.current = false;
    lastStartedAtRef.current = 0;
    send('stopped');
  }, [send]);

  const noteDraftValue = useCallback(
    (value: string) => {
      if (value.trim().length === 0) {
        stopTyping();
        return;
      }

      const now = Date.now();
      if (
        activeRef.current &&
        now - lastStartedAtRef.current < COMMENT_TYPING_REFRESH_MS
      ) {
        return;
      }

      activeRef.current = true;
      lastStartedAtRef.current = now;
      send('started');
    },
    [send, stopTyping],
  );

  useEffect(() => stopTyping, [stopTyping]);

  return { noteDraftValue, stopTyping };
}

export function useCommentTypingIndicators({
  cardId,
  currentUserId,
}: {
  readonly cardId: string;
  readonly currentUserId?: string | undefined;
}): {
  readonly typingUsers: readonly CommentTypingEntry[];
  readonly receiveTypingFrame: (frame: LiveSocketFrame) => void;
  readonly clearTypingUser: (userId: string) => void;
} {
  const [entries, setEntries] = useState<StoredCommentTypingEntry[]>([]);

  const clearExpired = useCallback(() => {
    const now = Date.now();
    setEntries((current) => current.filter((entry) => entry.expiresAtMs > now));
  }, []);

  const clearTypingUser = useCallback((userId: string) => {
    setEntries((current) => current.filter((entry) => entry.user.id !== userId));
  }, []);

  const receiveTypingFrame = useCallback(
    (frame: LiveSocketFrame) => {
      if (frame.type !== 'comment.typing') return;
      if (frame.cardId !== cardId) return;
      if (frame.user.id === currentUserId) return;

      const expiresAtMs = Date.parse(frame.expiresAt);
      if (!Number.isFinite(expiresAtMs) || frame.state === 'stopped') {
        clearTypingUser(frame.user.id);
        return;
      }

      const entry: StoredCommentTypingEntry = {
        user: frame.user,
        expiresAt: frame.expiresAt,
        expiresAtMs,
      };
      setEntries((current) => [
        ...current.filter((item) => item.user.id !== frame.user.id),
        entry,
      ]);
      clearExpired();
    },
    [cardId, clearExpired, clearTypingUser, currentUserId],
  );

  useEffect(() => {
    if (entries.length === 0) return undefined;
    const now = Date.now();
    const nextExpiry = Math.min(...entries.map((entry) => entry.expiresAtMs));
    const timer = setTimeout(clearExpired, Math.max(0, nextExpiry - now) + 25);
    return () => clearTimeout(timer);
  }, [clearExpired, entries]);

  return {
    typingUsers: entries.map(({ user, expiresAt }) => ({ user, expiresAt })),
    receiveTypingFrame,
    clearTypingUser,
  };
}
